import React from "react";
import { useNavigate } from "react-router-dom";
import { Activity, ArrowLeft, ShieldCheck } from "lucide-react";

const SECTIONS = [
  {
    h: "1. Who we are",
    p: "RadioShift is a staff-scheduling application for hospital radiology departments, built and operated by GSoftware LLC. This policy explains what information RadioShift collects, how it is used, and the choices you have.",
  },
  {
    h: "2. Information we collect",
    list: [
      "Account details: your name, email address, role (admin or technologist), certification category (CT, X-ray, or both), and employment type.",
      "Scheduling data: shifts assigned to you, shift preferences (day/night), time-off requests, and shift-swap requests.",
      "Communications: messages you send in the department chat and direct messages.",
      "Donations: if you choose to donate, payments are processed by Stripe. RadioShift stores only a payment reference and amount — never your card number.",
    ],
  },
  {
    h: "3. How we use your information",
    list: [
      "To build and display work schedules and enforce coverage and qualification rules.",
      "To process time-off and swap requests and notify the relevant people.",
      "To let staff communicate within the department.",
      "To operate, secure, and improve the service.",
    ],
  },
  {
    h: "4. Payment processing",
    p: "Donations are handled by Stripe, Inc. Your card details are entered on Stripe's secure checkout and are never seen or stored by RadioShift. Stripe's handling of your data is governed by Stripe's own privacy policy.",
  },
  {
    h: "5. Data sharing",
    p: "We do not sell your personal information. Scheduling and profile data are visible to your department administrators and, where relevant, to colleagues (e.g. a swap request shows your name). We share data with service providers (such as Stripe) only as needed to run the service.",
  },
  {
    h: "6. Data retention & security",
    p: "We keep your information for as long as your account is active or as needed to provide the service. Access is protected by authentication and role-based permissions. No system is perfectly secure, but we take reasonable measures to protect your data.",
  },
  {
    h: "7. Your choices",
    p: "You can ask your department administrator to update or remove your account information. For requests about your personal data, contact us using the details below.",
  },
  {
    h: "8. Contact",
    p: "Questions about this policy? Email privacy@langleyradioshift.com. RadioShift is built and operated by GSoftware LLC.",
  },
];

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  return (
    <div className="rs-grid-bg rs-scanlines min-h-screen text-white p-6 sm:p-10">
      <div className="max-w-3xl mx-auto relative z-[2] rs-fade-up">
        <button data-testid="privacy-back" onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-mono-rs uppercase mb-8">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex items-center gap-3 mb-2">
          <Activity className="w-7 h-7 text-[#D902EE] rs-text-glow-magenta" />
          <span className="font-mono-rs text-2xl font-bold tracking-tighter uppercase">
            Radio<span className="text-[#D902EE]">Shift</span>
          </span>
        </div>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-[#00F0FF]" />
          <h1 className="font-mono-rs text-2xl uppercase tracking-wide">Privacy Policy</h1>
        </div>
        <p className="text-xs text-slate-500 mb-10 font-mono-rs">Effective June 2026</p>

        <div className="space-y-8">
          {SECTIONS.map((s) => (
            <section key={s.h}>
              <h2 className="font-mono-rs text-sm uppercase tracking-[0.15em] text-[#00F0FF] mb-3">{s.h}</h2>
              {s.p && <p className="text-sm text-slate-300 leading-relaxed">{s.p}</p>}
              {s.list && (
                <ul className="space-y-2 mt-1">
                  {s.list.map((li, i) => (
                    <li key={i} className="text-sm text-slate-300 leading-relaxed flex gap-3">
                      <span className="text-[#D902EE] mt-1">▹</span><span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
